/**
 * Detail Line Tool - 2D Line Creation
 * 
 * Creates 2D lines in the view.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface DetailLineGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    style?: string;
}

export interface DetailLineElement extends Element {
    category: 'Detail Items';
    geometryData: DetailLineGeometryData;
}

export class DetailLineTool extends BaseTool {
    id = 'detail_line';
    name = 'Detail Line';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createDetailLine(this.startPoint, snapped);

            // Chain
            this.startPoint = snapped;
            this.currentPoint = snapped;
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'line',
                start: this.startPoint,
                end: this.currentPoint
                // No fixed color: the viewport draws line previews with the
                // theme ink (white in dark mode, black in light mode)
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createDetailLine(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const style = this.context.bimStore.toolProperties.detailLineStyle ?? 'Thin Lines';
        const geometryData: DetailLineGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            style,
        };

        const line: DetailLineElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: `Detail Line: ${style}`,
            parameters: {
                'Length': start.distanceTo(end).toFixed(2) + ' m',
                'Line Style': style,
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(line);
    }
}
